import { apiServerUrl, ExternalApi } from "@/apis/apiService";
import { usePiniaStore } from "@/stores/store";
import { useOrganisationStore } from "@/stores/organisationStore";
import { showAlert } from "@/methods/errorHandling";
import { keysToCamelCase, keysToSnakeCase } from "@/methods/stringProcessors";
import { HTTP_METHOD, PaymentMethods } from "@esenda/core-ui/constants";
import {
    BacsCreateProfileRequestPayload,
    BacsUpdateProfileRequestPayload,
} from "@/types/bacs";
import { OrganisationContactDetails } from "@/types/school";
import { getRequestConfig } from "@esenda/core-ui/utils";

/**
 * Returns an the profile information for an organisation based on
 * the give shortcode. It saves the organisation information in the
 * session store.
 * @param {string} shortcode
 */
export const getOrganisationSettings = async (shortcode: string) => {
    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getOrganisationSettings",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/profile`,
            HTTP_METHOD.GET,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        useOrganisationStore().setActiveOrganisationSettings(data);
        return data;
    } else {
        return null;
    }
};

/**
 * Returns the posting preferences of an organisation based on the
 * shortcode and it saves it to the session store.
 * @param {string} shortcode
 */
export const getOrganisationPostingsPreferences = async (shortcode: string) => {
    const pStore = usePiniaStore();

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getOrganisationPostingsPreferences",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/posting-preferences`,
            HTTP_METHOD.GET,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        pStore.setPostingCustomOptions(data);
        return data;
    } else {
        return null;
    }
};

/**
 * Updates the posting preferences of an organisation based on the
 * shortcode and it saves it to the session store.
 * @param {string} shortcode
 * @param {FormData} payload - posting preferences
 */
export const putOrganisationPostingPreferences = async (
    shortcode: string,
    payload: FormData,
) => {
    const pStore = usePiniaStore();

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "putOrganisationPostingPreferences",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/posting-preferences`,
            HTTP_METHOD.PUT,
            {},
            payload,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        pStore.setPostingCustomOptions(data);
        showAlert("alerts.updatedPostingPreferences", "success");
        return data;
    } else {
        return null;
    }
};

/**
 * Creates and returns API credentials for the BACS feature.
 * @param {string} shortcode
 */
export const createBacsCredentials = async (shortcode: string) => {
    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "createBacsCredentials",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/bacs-file-upload-credentials`,
            HTTP_METHOD.POST,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        showAlert("alerts.generatedApiKeys", "success");
        return data;
    } else {
        return null;
    }
};

/**
 * Used to upload a logo for an organisation which will be used in the
 * payments journey.
 * @param {string} shortcode
 * @param {File} file
 */
export const uploadOrganisationLogo = async (shortcode: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "uploadOrganisationLogo",
    };
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/logo`,
            HTTP_METHOD.PUT,
            { contentType: "multipart/form-data" },
            formData,
        ),
        errorHandlerConfig,
    );

    if (error === null) {
        showAlert("alerts.uploadedLogo", "success");
    }
    return { data, error };
};

/**
 * Returns the card limit settings of an organisation based on the
 * shortcode.
 * @param {string} shortcode
 */
export const getCardLimitSettings = async (shortcode: string) => {
    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getCardLimitSettings",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/card-limit`,
            HTTP_METHOD.GET,
        ),
        errorHandlerConfig,
    );

    if (data === null) {
        return null;
    }

    const cardLimit = keysToCamelCase(data);
    return cardLimit;
};

/**
 * Updates the card limit settings of an organisation based on the
 * shortcode.
 * @param {string} shortcode
 * @param {any} payload - card limit settings
 */
export const setCardLimitSettings = async (shortcode: string, payload: any) => {
    const pStore = usePiniaStore();
    const currencySymbol = pStore.getSchoolSettings.base_currency_symbol;

    const formattedPayload = keysToSnakeCase(payload);

    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "setCardLimitSettings",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/card-limit`,
            HTTP_METHOD.PUT,
            {},
            formattedPayload,
        ),
        errorHandlerConfig,
    );

    if (data === null) {
        return null;
    }

    const cardLimit = keysToCamelCase(data);
    if (cardLimit.cardLimitMax === null) {
        showAlert("alerts.cardLimitRemoved", "success");
    } else {
        showAlert("alerts.cardLimitSet", "success", {
            currency: currencySymbol,
            amount: (cardLimit.cardLimitMax / 100).toFixed(2),
        });
    }
    return cardLimit;
};

/**
 * Queries the API for provider specific information based on the provider
 * value passed. It returns an object containing provider specific information
 * @param {string} shortcode
 * @param {PaymentMethods} provider
 */
export const getProviderAccountInformation = async (
    shortcode: string,
    provider: PaymentMethods,
) => {
    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getProviderAccountInformation",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/accounts?provider=${provider}`,
            HTTP_METHOD.GET,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        return data;
    } else {
        return null;
    }
};

/**
 * Returns the billing providers details
 * @param {string} shortcode
 */
export const getFinancialAndBillingSystemsSettings = async (
    shortcode: string,
) => {
    /** Define the behaviour if an error occurs during the API call */
    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getFinancialAndBillingSystemsSettings",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/financial-and-billing-systems`,
            HTTP_METHOD.GET,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        const financialAndBillingSystemsSettings = keysToCamelCase(data);
        return financialAndBillingSystemsSettings;
    } else {
        return null;
    }
};

/**
 * Update the billing providers details
 * @param {string} shortcode
 * @param {any} requestPayload
 */
export const updateFinancialAndBillingSystemsSettings = async (
    shortcode: string,
    requestPayload: any,
) => {
    const payload = keysToSnakeCase(requestPayload);

    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: `updateFinancialAndBillingSystemsSettings`,
    };
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/financial-and-billing-systems`,
            HTTP_METHOD.PUT,
            {},
            payload,
        ),
        errorHandlerConfig,
    );

    if (error === null) {
        showAlert("alerts.detailsUpdated", "success");
        return data;
    } else return null;
};

/**
 * Update the billing providers details
 * @param {string} shortcode
 * @param {any} requestPayload
 */
export const testFinancialAndBillingSystemsSettingsIntegration = async (
    shortcode: string,
    requestPayload: any,
) => {
    const payload = keysToSnakeCase(requestPayload);

    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: `testFinancialAndBillingSystemsSettingsIntegration`,
    };
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/financial-and-billing-systems/test`,
            HTTP_METHOD.POST,
            {},
            payload,
        ),
        errorHandlerConfig,
    );

    if (error === null) {
        return keysToCamelCase(data);
    } else return null;
};

/**
 * Returns a list of BACS organisation accounts for an organisation.
 * @param {string} shortcode
 */
export const getBacsOrganisationAccounts = async (shortcode: string) => {
    const url = `${apiServerUrl}/organisations/${shortcode}/settings/bacs-accounts`;

    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getBacsOrganisationAccounts",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(url, HTTP_METHOD.GET),
        errorHandlerConfig,
    );

    return data ?? null;
};

/**
 * Triggers an API call to Smarterpay to return an up to date list of
 * organisation accounts.
 * @param {string} shortcode
 */
export const refreshBacsOrganisationAccounts = async (shortcode: string) => {
    const url = `${apiServerUrl}/organisations/${shortcode}/settings/bacs-accounts/refresh`;

    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "refreshBacsOrganisationAccounts",
    };
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(url, HTTP_METHOD.POST),
        errorHandlerConfig,
    );

    if (error === null) {
        return { status: "success", data };
    } else {
        return null;
    }
};

/**
 * Returns a list of BACS profiles for an organisation.
 * @param {string} shortcode
 */
export const getBacsProfiles = async (shortcode: string) => {
    const url = `${apiServerUrl}/organisations/${shortcode}/settings/bacs-profiles`;

    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getBacsProfiles",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(url, HTTP_METHOD.GET),
        errorHandlerConfig,
    );

    return data ?? null;
};

/**
 * Creates a BACS profile for an organisation.
 * @param {string} shortcode
 * @param {BacsCreateProfileRequestPayload} requestPayload
 */
export const createBacsProfile = async (
    shortcode: string,
    requestPayload: BacsCreateProfileRequestPayload,
) => {
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "createBacsProfile",
    };
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/bacs-profile`,
            HTTP_METHOD.POST,
            {},
            requestPayload,
        ),
        errorHandlerConfig,
    );

    if (error === null) {
        showAlert("alerts.bacsProfileCreated", "success");
        return data;
    } else return null;
};

/**
 * Updates a BACS profile for an organisation.
 * @param {string} shortcode
 * @param {string} profileId
 * @param {BacsUpdateProfileRequestPayload} requestPayload
 */
export const updateBacsProfile = async (
    shortcode: string,
    profileId: string,
    requestPayload: BacsUpdateProfileRequestPayload,
) => {
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "updateBacsProfile",
    };
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/bacs-profile/${profileId}`,
            HTTP_METHOD.PATCH,
            {},
            requestPayload,
        ),
        errorHandlerConfig,
    );

    if (error === null) {
        showAlert("alerts.bacsProfileUpdated", "success");
        return data;
    } else return null;
};

/**
 * Sends a new account name value to update a BACS organisation account.
 * @param {string} shortcode
 * @param {string} accountId
 * @param {string} accountName
 */
export const updateBacsAccount = async (
    shortcode: string,
    accountId: string,
    accountName: string,
) => {
    const url = `${apiServerUrl}/organisations/${shortcode}/settings/bacs-accounts/${accountId}`;

    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "updateBacsAccount",
    };
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(
            url,
            HTTP_METHOD.PATCH,
            {},
            { account_name: accountName },
        ),
        errorHandlerConfig,
    );

    if (error === null) {
        return { status: "success", data };
    } else return null;
};

/**
 * Returns the contact details for an organisation.
 * @param {string} shortcode
 */
export const getContactDetails = async (shortcode: string) => {
    const url = `${apiServerUrl}/organisations/${shortcode}/settings/contact-details`;

    const errorHandlerConfig = {
        userAction: "refresh", // user needs to retry the action
        sentryTransactionName: "getContactDetails",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(url, HTTP_METHOD.GET),
        errorHandlerConfig,
    );

    return data ?? null;
};

/**
 * Updates contact details for an organisation.
 * @param {string} shortcode
 * @param {OrganisationContactDetails} requestPayload
 */
export const updateContactDetails = async (
    shortcode: string,
    requestPayload: OrganisationContactDetails,
) => {
    const errorHandlerConfig = {
        userAction: "retry", // user needs to retry the action
        sentryTransactionName: "updateContactDetails",
    };
    const externalApi = new ExternalApi();
    const { data } = await externalApi.callExternalApi(
        getRequestConfig(
            `${apiServerUrl}/organisations/${shortcode}/settings/contact-details`,
            HTTP_METHOD.PUT,
            {},
            requestPayload,
        ),
        errorHandlerConfig,
    );

    if (data !== null) {
        showAlert("alerts.contactDetailsUpdated", "success");
        return data;
    } else return null;
};
